import {Style} from '@frontapp/editor-html';
import _ from 'lodash';
import React, {FC, PropsWithChildren, ReactNode, useLayoutEffect, useRef, useState} from 'react';
import styled from 'styled-components';

import {KeyboardActionsEnum} from '../../../../../libs/shared/core/src/helpers/browser/keyboardHelpers';
import {Trans} from '../../../../../libs/shared/core/src/helpers/internationalization/react';
import {ToolbarButton} from '../../../../../libs/shared/ui/src/components/buttons/toolbarButton';
import {DropdownSeparator} from '../../../../../libs/shared/ui/src/components/dropdowns/dropdownSeparator';
import {SimpleDropdownItem} from '../../../../../libs/shared/ui/src/components/dropdowns/simpleDropdown/simpleDropdownItem';
import {IconName} from '../../../../../libs/shared/ui/src/components/icon/icon';
import {KeyboardEventHandlers} from '../../../../../libs/shared/ui/src/components/keyboard/keyboardContext';
import {KeyboardShortcuts} from '../../../../../libs/shared/ui/src/components/keyboard/keyboardShortcuts';
import {TooltipShortcutFormatter} from '../../../../../libs/shared/ui/src/components/tooltips/tooltipShortcutFormatter';
import {useContentSize} from '../../../../../libs/shared/ui/src/hooks/sizeHooks';
import {disabledCss} from '../../../../../libs/shared/ui/src/styles/mixins';
import {UserThemeConsumer} from '../../../../../libs/shared/ui/src/theme/userThemeConsumer';
import {HtmlStyle} from '../../../common/editor/style/htmlStyle';
import {MarkdownStyle} from '../../../common/editor/style/markdownStyle';
import {FlexSpacer} from '../../../common/flex';
import {FormattingControlsEnum} from './formattingConstants';
import {FormattingFontFamily} from './formattingFontFamily';
import {FormattingFontSize} from './formattingFontSize';
import {FormattingHighlightColor} from './formattingHighlightColor';
import {FormattingImage, FormattingImageProps, renderFormattingImageDropdownItems} from './formattingImage';
import {FormattingList, FormattingListProps, renderFormattingListDropdownItems} from './formattingList';
import {FormattingMoreDropdownButton} from './formattingMoreDropdownButton';
import {FormattingTextColor} from './formattingTextColor';

/*
 * Props.
 */

interface FormattingToolbarProps extends FormattingListProps, FormattingImageProps {
  isVisible: boolean;
  isFloating: boolean;
  isSimplified: boolean;
  isKeyboardShortcutsEnabled: boolean;
  htmlStyle?: HtmlStyle;
  markdownStyle?: Style;
  onStyleChange: ((style: HtmlStyle) => void) | ((style: MarkdownStyle) => void);
  isLinkButtonActive: boolean;
  onLinkButtonClick: () => void;
  isQuoteButtonActive: boolean;
  onQuoteButtonClick: (arg: boolean) => void;
  onResetStyleClick?: () => void;
  onSaveDefaultStyleClick?: () => void;
  renderMoreDropdownItems?: () => ReactNode;
  shouldRenderDropdowns: boolean;
  controlsToRender: Set<string>;
  isDisabled?: boolean;
}

/*
 * Constants.
 */

const MAYBE_HIDDEN_BUTTON_GAP_COMPACT = 4;
const MAYBE_HIDDEN_BUTTON_GAP_REGULAR = 6;
const MAYBE_HIDDEN_BUTTON_WIDTH = 30;
const MAYBE_HIDDEN_BUTTON_HEIGHT = 30;

const getHidableControls = (isSimplified: boolean) =>
  _.compact([
    FormattingControlsEnum.BOLD,
    FormattingControlsEnum.ITALIC,
    FormattingControlsEnum.UNDERLINE,
    FormattingControlsEnum.STRIKE,
    FormattingControlsEnum.LIST,
    FormattingControlsEnum.LINK,
    !isSimplified && FormattingControlsEnum.IMAGE,
    FormattingControlsEnum.BLOCKQUOTE,
    !isSimplified && FormattingControlsEnum.REMOVE,
  ]);

type HidableControlKey = ReturnType<typeof getHidableControls>[any];

type BooleanStyleProp = {[K in keyof Style]-?: Style[K] extends boolean | undefined ? K : never}[keyof Style];
interface BooleanFormattingControl {
  label: ReactNode;
  keyboardAction?: KeyboardActionsEnum;
  prop: BooleanStyleProp;
  iconName: IconName;
  key: HidableControlKey;
}

const booleanFormattingControls: Array<BooleanFormattingControl> = [
  {
    key: FormattingControlsEnum.BOLD,
    iconName: 'formattingBold',
    label: <Trans id="formatting-bold">Bold</Trans>,
    keyboardAction: KeyboardActionsEnum.EDITOR_BOLD,
    prop: 'isBold',
  },
  {
    key: FormattingControlsEnum.ITALIC,
    iconName: 'formattingItalic',
    label: <Trans id="formatting-italic">Italic</Trans>,
    keyboardAction: KeyboardActionsEnum.EDITOR_ITALIC,
    prop: 'isItalic',
  },
  {
    key: FormattingControlsEnum.UNDERLINE,
    iconName: 'formattingUnderline',
    label: <Trans id="formatting-underline">Underline</Trans>,
    keyboardAction: KeyboardActionsEnum.EDITOR_UNDERLINE,
    prop: 'hasUnderline',
  },
  {
    key: FormattingControlsEnum.STRIKE,
    iconName: 'formattingStrikethrough',
    label: <Trans id="formatting-strikethrough">Strikethrough</Trans>,
    prop: 'hasStrikethrough',
  },
];

const linkButtonLabel = <Trans id="formatting-link">Insert link</Trans>;

/*
 * Style.
 */

const StyledToolbarWrapperDiv = styled.div<{$isDisabled: boolean | undefined; $isFloating: boolean}>`
  flex-shrink: ${(p) => (p.$isFloating ? 1 : 0)};

  background-color: ${(p) => (!p.$isFloating && p.theme.message.background) || undefined};
  ${(p) => p.$isDisabled && disabledCss}
`;
const StyledToolbarBorderDiv = styled.div`
  border-top: 1px solid ${(p) => p.theme.message.border};
`;

const StyledContentWrapperDiv = styled.div<{$isShorter: boolean; $isCompact: boolean}>`
  display: flex;
  align-items: center;
  margin-left: ${(p) => (p.$isCompact ? '4px' : '8px')};
  margin-right: ${(p) => (p.$isCompact ? '4px' : '8px')};
  min-height: ${(p) => (p.$isShorter ? '38px' : '46px')};
`;

const StyledDropdownsWrapperDiv = styled.div<{$isCompact: boolean}>`
  display: flex;
  flex-shrink: 0;
  gap: ${(p) => (p.$isCompact ? '4px' : '6px')};
`;

const StyledMaybeVisibleButtonsWrapperDiv = styled.div<{$isCompact: boolean}>`
  display: flex;
  overflow: hidden;
  height: ${MAYBE_HIDDEN_BUTTON_HEIGHT}px;
  flex-grow: 1;
  flex-shrink: 1;
  flex-wrap: wrap;
  gap: ${(p) =>
    p.$isCompact ? `${MAYBE_HIDDEN_BUTTON_GAP_COMPACT}px` : `${MAYBE_HIDDEN_BUTTON_GAP_REGULAR}px`};
  padding-left: ${(p) =>
    p.$isCompact ? `${MAYBE_HIDDEN_BUTTON_GAP_COMPACT}px` : `${MAYBE_HIDDEN_BUTTON_GAP_REGULAR}px`};
`;

const StyledMoreButtonWrapperDiv = styled.div<{$isCompact: boolean}>`
  display: flex;
  margin-left: ${(p) =>
    p.$isCompact ? `${MAYBE_HIDDEN_BUTTON_GAP_COMPACT}px` : `${MAYBE_HIDDEN_BUTTON_GAP_REGULAR}px`};
`;

// If a button is to be hidden into the more dropdown, we want to keep the element in the DOM
// but remove it from the flow to not take up space, so we need position: absolute.
// We can't use display: none because we need to keep the width for our calculation
interface MaybeVisibleButtonStyleProps {
  $isHidden: boolean;
  $shouldNotBeRendered: boolean;
}
const StyledMaybeVisibleButtonWrapperDiv = styled.div<MaybeVisibleButtonStyleProps>`
  ${(props) => (props.$isHidden ? `visibility: hidden; position: absolute` : '')}
  ${(props) => (props.$shouldNotBeRendered ? `display: none` : '')}
`;

/*
 * Component.
 */

export const FormattingToolbar: FC<PropsWithChildren<FormattingToolbarProps>> = (props) => (
  <KeyboardShortcuts handlers={getKeyboardHandlers(props)} isDisabled={!props.isKeyboardShortcutsEnabled}>
    {props.isVisible && <FormattingToolbarButtons {...props} />}
  </KeyboardShortcuts>
);

function getKeyboardHandlers(props: FormattingToolbarProps): KeyboardEventHandlers {
  const {htmlStyle, markdownStyle, onStyleChange, onLinkButtonClick} = props;
  const style = htmlStyle || markdownStyle;

  return {
    [KeyboardActionsEnum.EDITOR_BOLD]: () => style && onStyleChange({isBold: !style.isBold}),
    [KeyboardActionsEnum.EDITOR_ITALIC]: () => style && onStyleChange({isItalic: !style.isItalic}),
    [KeyboardActionsEnum.EDITOR_UNDERLINE]: () => style && onStyleChange({hasUnderline: !style.hasUnderline}),
    [KeyboardActionsEnum.EDITOR_INSERT_LINK]: onLinkButtonClick,
  };
}

const FormattingToolbarButtons = (props: FormattingToolbarProps) => {
  const [hideStartIndex, setHideStartIndex] = useState<number | undefined>(undefined);
  const maybeVisibleButtonsWrapperRef = useRef<HTMLDivElement>(null);
  const {width: maybeVisibleButtonsWrapperWidth} = useContentSize(maybeVisibleButtonsWrapperRef, true);
  const hiddenControls = getHiddenControls(props.isSimplified, hideStartIndex);

  useLayoutEffect(() => {
    if (!maybeVisibleButtonsWrapperRef.current || maybeVisibleButtonsWrapperWidth === undefined) {
      return;
    }

    const buttonGap = props.isSimplified ? MAYBE_HIDDEN_BUTTON_GAP_COMPACT : MAYBE_HIDDEN_BUTTON_GAP_REGULAR;

    const newStartIndex = Math.floor(
      maybeVisibleButtonsWrapperWidth / (MAYBE_HIDDEN_BUTTON_WIDTH + buttonGap),
    );

    setHideStartIndex(newStartIndex);
  }, [hiddenControls, maybeVisibleButtonsWrapperWidth, props.isFloating, props.isSimplified]);

  return (
    <StyledToolbarWrapperDiv
      $isDisabled={props.isDisabled}
      $isFloating={props.isFloating}
      onMouseDown={(e) => {
        if (props.isFloating) {
          e.preventDefault();
        }
      }}
    >
      {!props.isFloating && <StyledToolbarBorderDiv />}
      <StyledContentWrapperDiv
        $isCompact={props.isSimplified}
        $isShorter={props.isSimplified && props.isFloating}
      >
        <StyledDropdownsWrapperDiv $isCompact={props.isSimplified}>
          {props.shouldRenderDropdowns && renderDropdowns(props)}
        </StyledDropdownsWrapperDiv>
        <StyledMaybeVisibleButtonsWrapperDiv
          ref={maybeVisibleButtonsWrapperRef}
          $isCompact={props.isSimplified}
        >
          {renderMaybeVisibleButtons(props, hiddenControls)}
        </StyledMaybeVisibleButtonsWrapperDiv>
        <FlexSpacer />
        <StyledMoreButtonWrapperDiv $isCompact={props.isSimplified}>
          {props.shouldRenderDropdowns && renderMoreDropdownButton(props, hiddenControls)}
        </StyledMoreButtonWrapperDiv>
      </StyledContentWrapperDiv>
    </StyledToolbarWrapperDiv>
  );
};

function renderDropdowns(props: FormattingToolbarProps) {
  const {htmlStyle: style, onStyleChange, isSimplified, isFloating} = props;
  return (
    <>
      <FormattingFontFamily
        isCompact={isSimplified}
        isInFloatingToolbar={isFloating}
        fontFamily={style && style.fontFamily}
        onFontFamilySelect={(fontFamily) => onStyleChange && onStyleChange({fontFamily})}
      />
      <FormattingFontSize
        isInFloatingToolbar={isFloating}
        fontSize={style && style.fontSize}
        onFontSizeSelect={(fontSize) => onStyleChange && onStyleChange({fontSize})}
      />
      <FormattingTextColor
        isInFloatingToolbar={isFloating}
        textColor={style && style.textColor}
        onTextColorSelect={(textColor) => onStyleChange && onStyleChange({textColor})}
        isCompact={isSimplified}
      />
      <FormattingHighlightColor
        isInFloatingToolbar={isFloating}
        highlightColor={style && style.highlightColor}
        onHighlightColorSelect={(highlightColor) => onStyleChange && onStyleChange({highlightColor})}
        isCompact={isSimplified}
      />
    </>
  );
}

function renderMaybeVisibleButtons(props: FormattingToolbarProps, hiddenControls: Set<HidableControlKey>) {
  const {
    htmlStyle,
    markdownStyle,
    onStyleChange,
    isLinkButtonActive,
    onLinkButtonClick,
    isQuoteButtonActive,
    onQuoteButtonClick,
    onResetStyleClick,
    controlsToRender,
  } = props;

  const style = htmlStyle || markdownStyle;

  return (
    <>
      {booleanFormattingControls.map((control) => (
        <UserThemeConsumer key={control.key}>
          {(theme) => (
            <StyledMaybeVisibleButtonWrapperDiv
              $shouldNotBeRendered={!controlsToRender.has(control.key)}
              $isHidden={hiddenControls.has(control.key)}
            >
              <ToolbarButton
                noMargin
                iconName={control.iconName}
                isActive={style && style[control.prop]}
                onClick={() => onStyleChange({[control.prop]: style && !style[control.prop]})}
                label={control.label}
                // Don't bind the keyboard action, but do display it in the tooltip.
                renderTooltip={() => renderButtonTooltip(control.label, control.keyboardAction)}
                shouldPreventMouseDown
                color={theme.greys.shade80}
                hoverColor={theme.greys.shade80}
              />
            </StyledMaybeVisibleButtonWrapperDiv>
          )}
        </UserThemeConsumer>
      ))}

      <StyledMaybeVisibleButtonWrapperDiv
        $shouldNotBeRendered={!controlsToRender.has(FormattingControlsEnum.LIST)}
        $isHidden={hiddenControls.has(FormattingControlsEnum.LIST)}
      >
        <FormattingList {...props} />
      </StyledMaybeVisibleButtonWrapperDiv>

      <UserThemeConsumer>
        {(theme) => (
          <StyledMaybeVisibleButtonWrapperDiv
            $shouldNotBeRendered={!controlsToRender.has(FormattingControlsEnum.LINK)}
            $isHidden={hiddenControls.has(FormattingControlsEnum.LINK)}
          >
            <ToolbarButton
              noMargin
              iconName="formattingLink"
              isActive={isLinkButtonActive}
              onClick={onLinkButtonClick}
              label={linkButtonLabel}
              // Don't bind the keyboard action, but do display it in the tooltip.
              renderTooltip={() =>
                renderButtonTooltip(linkButtonLabel, KeyboardActionsEnum.EDITOR_INSERT_LINK)
              }
              shouldPreventMouseDown
              color={theme.greys.shade80}
              hoverColor={theme.greys.shade80}
            />
          </StyledMaybeVisibleButtonWrapperDiv>
        )}
      </UserThemeConsumer>

      {!props.isSimplified && (
        <StyledMaybeVisibleButtonWrapperDiv
          $shouldNotBeRendered={!controlsToRender.has(FormattingControlsEnum.IMAGE)}
          $isHidden={hiddenControls.has(FormattingControlsEnum.IMAGE)}
        >
          <FormattingImage
            isImageSelectedInComposer={props.isImageSelectedInComposer}
            onInsertImageUrlInlineClick={props.onInsertImageUrlInlineClick}
            onUploadImageInlineClick={props.onUploadImageInlineClick}
          />
        </StyledMaybeVisibleButtonWrapperDiv>
      )}

      <UserThemeConsumer>
        {(theme) => (
          <StyledMaybeVisibleButtonWrapperDiv
            $shouldNotBeRendered={!controlsToRender.has(FormattingControlsEnum.BLOCKQUOTE)}
            $isHidden={hiddenControls.has(FormattingControlsEnum.BLOCKQUOTE)}
          >
            <ToolbarButton
              noMargin
              iconName="formattingBlockquote"
              isActive={isQuoteButtonActive}
              onClick={() => onQuoteButtonClick(!isQuoteButtonActive)}
              label={<Trans id="formatting-blockquote">Quote</Trans>}
              shouldPreventMouseDown
              color={theme.greys.shade80}
              hoverColor={theme.greys.shade80}
            />
          </StyledMaybeVisibleButtonWrapperDiv>
        )}
      </UserThemeConsumer>

      <UserThemeConsumer>
        {(theme) => (
          <StyledMaybeVisibleButtonWrapperDiv
            $shouldNotBeRendered={!controlsToRender.has(FormattingControlsEnum.REMOVE) || props.isSimplified}
            $isHidden={hiddenControls.has(FormattingControlsEnum.REMOVE)}
          >
            <ToolbarButton
              noMargin
              iconName="formattingRemove"
              onClick={onResetStyleClick}
              label={<Trans id="formatting-remove">Reset style</Trans>}
              shouldPreventMouseDown
              color={theme.greys.shade80}
              hoverColor={theme.greys.shade80}
            />
          </StyledMaybeVisibleButtonWrapperDiv>
        )}
      </UserThemeConsumer>
    </>
  );
}

function renderButtonTooltip(label: ReactNode, keyboardAction: KeyboardActionsEnum | undefined) {
  if (!keyboardAction) {
    return label;
  }

  return (
    <>
      {label} <TooltipShortcutFormatter action={keyboardAction} />
    </>
  );
}

function renderMoreDropdownButton(props: FormattingToolbarProps, hiddenControls: Set<HidableControlKey>) {
  const {
    htmlStyle,
    markdownStyle,
    onStyleChange,
    isLinkButtonActive,
    onLinkButtonClick,
    isQuoteButtonActive,
    onQuoteButtonClick,
    onResetStyleClick,
    onSaveDefaultStyleClick,
    renderMoreDropdownItems,
    isSimplified,
    isFloating,
  } = props;
  const style = htmlStyle || markdownStyle;

  return (
    <FormattingMoreDropdownButton
      placement={(isSimplified && 'bottom-start') || undefined}
      isInFloatingToolbar={isFloating}
    >
      {booleanFormattingControls
        .filter((control) => hiddenControls.has(control.key))
        .map((control) => (
          <SimpleDropdownItem
            key={control.key}
            isChecked={style && style[control.prop]}
            onSelect={() => onStyleChange({[control.prop]: style && !style[control.prop]})}
          >
            {control.label}
          </SimpleDropdownItem>
        ))}

      {hiddenControls.has(FormattingControlsEnum.STRIKE) && <DropdownSeparator />}

      {hiddenControls.has(FormattingControlsEnum.LIST) && (
        <>
          {renderFormattingListDropdownItems(props)}
          <DropdownSeparator />
        </>
      )}

      {hiddenControls.has(FormattingControlsEnum.LINK) && (
        <SimpleDropdownItem isChecked={isLinkButtonActive} onSelect={onLinkButtonClick}>
          <Trans id="formatting-link-menu">Insert link</Trans>
        </SimpleDropdownItem>
      )}

      {hiddenControls.has(FormattingControlsEnum.IMAGE) && (
        <>
          {renderFormattingImageDropdownItems(props)}
          <DropdownSeparator />
        </>
      )}

      {hiddenControls.has(FormattingControlsEnum.BLOCKQUOTE) && (
        <SimpleDropdownItem
          isChecked={isQuoteButtonActive}
          onSelect={() => onQuoteButtonClick(!isQuoteButtonActive)}
        >
          <Trans id="formatting-blockquote">Quote</Trans>
        </SimpleDropdownItem>
      )}

      {(isSimplified || hiddenControls.has(FormattingControlsEnum.REMOVE)) && (
        <>
          <SimpleDropdownItem onSelect={onResetStyleClick}>
            <Trans id="formatting-remove">Reset style</Trans>
          </SimpleDropdownItem>
          {!isSimplified && <DropdownSeparator />}
        </>
      )}
      {onSaveDefaultStyleClick && (
        <SimpleDropdownItem onSelect={onSaveDefaultStyleClick}>
          <Trans id="formatting-savedefaultfontstyle">Save default style</Trans>
        </SimpleDropdownItem>
      )}

      {renderMoreDropdownItems && renderMoreDropdownItems()}
    </FormattingMoreDropdownButton>
  );
}

/*
 * Helpers.
 */

function getHiddenControls(
  isSimplified: boolean,
  hideStartIndex: number | undefined,
): Set<HidableControlKey> {
  if (!hideStartIndex) {
    return new Set();
  }
  return new Set(getHidableControls(isSimplified).slice(hideStartIndex));
}
